import { useState } from "react";
import { V2, ageSeconds } from "./tokens";

/* Shared V2 shell primitives (Figma 47:2 section grammar). */

export const Eyebrow = ({ color, children, size = 12 }: { color: string; children: React.ReactNode; size?: number }) => (
  <p className="nl-geist font-semibold uppercase"
    style={{ color, fontSize: size, lineHeight: `${size + 4}px`, letterSpacing: size >= 12 ? "0.07em" : "0.1em" }}>
    {children}
  </p>
);

export const SectionTitle = ({ children, size = 38 }: { children: React.ReactNode; size?: number }) => (
  <h2 className="nl-sg font-medium mt-[14px]"
    style={{ color: V2.text, fontSize: size, lineHeight: `${size + 6}px` }}>
    {children}
  </h2>
);

export const SectionDeck = ({ children }: { children: React.ReactNode }) => (
  <p className="nl-geist text-[16px] leading-[25px] mt-[10px] max-w-[900px]" style={{ color: V2.body }}>
    {children}
  </p>
);

export const HairLine = ({ className = "" }: { className?: string }) => (
  <div aria-hidden className={`h-px w-full ${className}`} style={{ background: V2.border2 }} />
);

export const Pill = ({ color, children, testid }: { color: string; children: React.ReactNode; testid?: string }) => (
  <span data-testid={testid}
    className="nl-geist inline-flex items-center h-[28px] px-[12px] rounded-full text-[10px] leading-[12px] font-semibold uppercase"
    style={{ color, border: `1px solid ${color}`, background: `${color}17` }}>
    {children}
  </span>
);

export const FreshnessChip = ({ minutes, testid }: { minutes: number | null; testid?: string }) => {
  if (minutes == null) return null;
  return (
    <span data-testid={testid}
      className="nl-geist text-[9px] leading-[12px] font-semibold uppercase tracking-[0.1em]"
      style={{ color: V2.faint }}>
      UPDATED {minutes < 1 ? "NOW" : minutes < 60 ? `${minutes}M AGO` : `${Math.floor(minutes / 60)}H AGO`}
    </span>
  );
};

/* True-LIVE per-item chip (Gate-1 §F): LIVE only when payload age <=150s,
   otherwise a truthful UPDATED chip. Families never inherit each other's LIVE. */
export const LiveChip = ({ updatedAt, testid }: { updatedAt?: string | null; testid?: string }) => {
  const s = ageSeconds(updatedAt);
  if (s == null) return null;
  if (s <= 150) {
    return (
      <span data-testid={testid}
        className="nl-geist inline-flex items-center gap-[5px] text-[9px] leading-[12px] font-semibold uppercase tracking-[0.1em]"
        style={{ color: V2.pos }}>
        <span aria-hidden className="w-[5px] h-[5px] rounded-full animate-pulse" style={{ background: V2.pos }} />
        LIVE
      </span>
    );
  }
  const m = Math.floor(s / 60);
  return (
    <span data-testid={testid}
      className="nl-geist text-[9px] leading-[12px] font-semibold uppercase tracking-[0.1em]"
      style={{ color: V2.faint }}>
      UPDATED {m < 1 ? "NOW" : m < 60 ? `${m}M AGO` : `${Math.floor(m / 60)}H AGO`}
    </span>
  );
};

/* Deterministic dot-field fallback (Figma article-visual treatment) — used
   only when the shared media chain (lead_visual -> hero_image_url) is empty. */
export const DotFieldArt = ({ label = true }: { label?: boolean }) => (
  <div aria-hidden className="relative w-full h-full overflow-hidden"
    style={{ background: "linear-gradient(120deg,#1c0a47 0%,#1a1a38 55%,#004761 100%)" }}>
    {[...Array(7)].map((_, i) => (
      <span key={i} className="absolute rounded-full"
        style={{
          left: `${8 + i * 12.5}%`, top: `${i % 3 === 0 ? 20 : i % 3 === 1 ? 33 : 46}%`,
          width: 34 + (i % 3) * 14, height: 34 + (i % 3) * 14,
          background: i % 2 ? "#33ccf2" : "#8c47ff", opacity: 0.18 + i * 0.06,
        }} />
    ))}
    {label && (
      <span className="nl-geist absolute left-[24px] bottom-[16px] text-[10px] leading-[14px] font-semibold tracking-[0.1em] text-[#9696a8] uppercase">
        NEWSLAYER VISUAL
      </span>
    )}
  </div>
);

export const ArticleMedia = ({ article, className = "", eager = false }: { article: any; className?: string; eager?: boolean }) => {
  const src = article?.lead_visual?.url || article?.hero_image_url || null;
  const [failed, setFailed] = useState(false);
  return (
    <div className={`overflow-hidden ${className}`} style={{ background: "#0a0a12" }}>
      {src && !failed ? (
        <img src={src} alt="" loading={eager ? "eager" : "lazy"} decoding="async"
          {...(eager ? { fetchPriority: "high" } as any : {})}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)} />
      ) : (
        <DotFieldArt />
      )}
    </div>
  );
};
